import * as React from 'react';

import { Card, formatMoney, TinyTable, TinyTableProps } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';

import { $overviewFinancialAccounts } from '../../_state/financeOverview.state';

import { useTranslations } from '@/finance/i18n/useTranslations';

type AccountRow = NonNullable<
  NonNullable<
    ReturnType<typeof useAtomValue<typeof $overviewFinancialAccounts>>['data']
  >['accounts']
>[number];

type TinyTableColumn<T> = TinyTableProps<T>['columns'][number];

export const OverviewFinancialAccounts = () => {
  const t = useTranslations();
  const { data, isFetching } = useAtomValue($overviewFinancialAccounts);

  const accounts = data?.accounts ?? [];

  const columns: TinyTableColumn<AccountRow>[] = React.useMemo(
    () => [
      {
        key: 'name',
        label: t.overview.financialAccounts.columns.name,
        width: '1fr',
        cell: (row) => <span className="font-medium">{row.name}</span>,
      },
      {
        key: 'type',
        label: t.overview.financialAccounts.columns.type,
        width: 'auto',
        cell: (row) => {
          const typeMap: Record<string, string> = {
            Cash: t.financialAccounts.list.typeFilter.options.cash,
            Checking: t.financialAccounts.list.typeFilter.options.checking,
            Savings: t.financialAccounts.list.typeFilter.options.savings,
          };
          return <span>{typeMap[row.type] ?? row.type}</span>;
        },
      },
      {
        key: 'balance',
        label: t.overview.financialAccounts.columns.balance,
        width: 'auto',
        align: 'right' as const,
        cell: (row) =>
          formatMoney(
            row.currentBalance.amount,
            row.currentBalance.currencyCode,
          ),
      },
    ],
    [t],
  );

  return (
    <Card className="@OverviewFinancialAccounts flex h-full flex-col">
      <h3 className="text-lg font-semibold">
        {t.overview.financialAccounts.title}
      </h3>
      <p className="text-sm text-neutral">
        {t.overview.financialAccounts.subtitle}
      </p>
      {!isFetching && accounts.length === 0 ? (
        <p className="py-8 text-center text-sm text-neutral">
          {t.overview.financialAccounts.empty}
        </p>
      ) : (
        <TinyTable
          data={accounts}
          isLoading={isFetching}
          rowKey={(row) => row.id}
          columns={columns}
          className="overflow-y-auto"
          headerClassName="bg-neutral/8 font-semibold text-neutral [&_*]:opacity-100"
          rowClassName="bg-white"
        />
      )}
    </Card>
  );
};
